import './BlogDetail.scss'
import React, { useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { css, cx, useTheme } from '@fable/theme'

import { BLOCKS, INLINES } from '@contentful/rich-text-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'

import { renderParticipantsTag, renderPostTag } from '../../utils'
import {
  descriptionStyles,
  getSlug,
  renderVideo,
  formatDate,
} from '../../../../utils'
import { GET_POST, GET_POST_ID } from '../../queries/blog'
import { routes as communityRoutes } from '../../routes'
import { routes as storeRoutes } from 'app/store/routes'
import PlayIcon from '../../../../assets/icons/components/PlayIcon'
import ChevronIcon from '../../../../assets/icons/components/ChevronIcon'

import Loader, { LoaderType } from '../../../../components/loader'
import ShareButton from '../../../../components/share_button/ShareButton'

import { TITLES } from '../../../../constants'
import useSlugQuery from '../../../../hooks/useSlugQuery'
import { PostBookList } from '../../../../components/post_book_list'
import { useActions, useTypedSelector } from '../../../../hooks'
import errorCardModalPayload from '../../../../components/error_card/errorCardModalPayload'
import { Page } from '../../../../components/Page'
import { FlexBox, RoundImage, Text } from '@fable/components'

const BlogDetail = () => {
  const { colors } = useTheme()
  const location = useLocation()
  const { openModal } = useActions()
  const modal = useTypedSelector(({ modal }) => modal)
  const [isPlayingVideo, setIsPlayingVideo] = useState(false)
  const path = location.pathname
  const slug = getSlug(path)
  const { data, error, loading } = useSlugQuery(
    GET_POST_ID,
    slug,
    'blogPostCollection',
    GET_POST
  )

  const mediaNode = useRef()

  if (loading) return <Loader type={LoaderType.fullscreen} />

  const playVideo = () => {
    if (!isPlayingVideo) {
      setIsPlayingVideo(true)
      const iframe = mediaNode.current.children[0]
      iframe.src += '?autoplay=1'
    }
  }

  const renderMedia = (video, featuredImage) => {
    if (video) {
      const play = isPlayingVideo ? 'play' : ''

      return (
        <div
          className={`blog-post-media video ${play}`}
          onClick={playVideo}
          ref={mediaNode}
        >
          {renderVideo(video)}
          <img src={featuredImage.url} alt={featuredImage.title} />
          <PlayIcon />
        </div>
      )
    }

    return (
      <div className="blog-post-media">
        <img src={featuredImage.url} alt={featuredImage.title} />
      </div>
    )
  }

  const renderParticipants = (participants) => {
    if (participants.length === 0) {
      return
    }

    const tag = renderParticipantsTag(participants)

    const participantBodyOptions = {
      renderNode: {
        [BLOCKS.PARAGRAPH]: (node, children) => (
          <Text type="body" sizing="M">
            {children}
          </Text>
        ),
      },
    }

    const renderList = participants.map((participant, index) => {
      const { avatar, bio, name } = participant

      return (
        <div key={index} className="participant">
          <div className="participant-header">
            <img className="avatar" src={avatar.url} alt={avatar.title} />
            <Text type="body" sizing="M">
              {name}
            </Text>
          </div>
          <div className="participant-bio">
            {documentToReactComponents(bio.json, participantBodyOptions)}
          </div>
        </div>
      )
    })

    return (
      <>
        <div className="blog-post-participants">
          {tag}
          <div className="participants-list">{renderList}</div>
        </div>
        <hr />
      </>
    )
  }

  const renderContent = () => {
    const item = data?.blogPost
    if (error || !item) {
      if (!modal?.isOpen) {
        const { content, modalType } = errorCardModalPayload(
          !data?.blogPost ? 'Blog post not found' : ''
        )
        openModal(content, modalType)
      }
      return <div />
    }

    const {
      author,
      body,
      featuredImage,
      participantsCollection,
      postType,
      publishDate,
      title,
      video,
    } = item

    const embeddedAssets = body?.links?.assets?.block

    let embeddedAssetCount = 0

    const richTextRenderOptions = {
      renderNode: {
        ...descriptionStyles.renderNode,
        [BLOCKS.EMBEDDED_ASSET]: () => {
          if (!embeddedAssets) {
            return null
          }
          const asset = embeddedAssets[embeddedAssetCount]
          embeddedAssetCount++
          if (!asset) {
            return null
          }
          if (asset.contentType.startsWith('image/')) {
            return (
              <img
                src={asset.url}
                alt={asset.title}
                style={{ width: asset.width, maxWidth: '100%' }}
              />
            )
          } else if (asset.contentType.startsWith('video/')) {
            // todo, ignore embedded video for now
            return null
          }
        },
        [INLINES.EMBEDDED_ENTRY]: (node) => {
          const id = node.data.target.sys.id
          return <PostBookList className={cx(styles.bookList)} id={id} />
        },
      },
    }

    const renderDate = formatDate(publishDate)

    return (
      <>
        <section className="blog-post-masthead wrap masthead light pad-edges">
          <Link
            className="back-button button mini solid"
            to={communityRoutes.blog}
          >
            <ChevronIcon /> {TITLES.community.blog}
          </Link>
          {renderPostTag(postType)}
          <Text
            className={css`
              padding: 0 16px;
              max-width: 520px;
            `}
            type="header"
            sizing={{ base: 'M', mobileL: 'L', desktop: 'XL' }}
            text={title}
            align="center"
          />
          <div className="blog-post-details">
            <Text className="blog-post-author" type="label" sizing="L">
              {author.name}
            </Text>
            <Text className="blog-post-date" type="label" sizing="L">
              {renderDate}
            </Text>
          </div>
          <ShareButton category="post" title="Share" secondary />
        </section>
        <section className="blog-post-content-wrap wrap simple overlap">
          <div className="blog-post-content content-wrap content-pad-edges overlap fade">
            {renderMedia(video, featuredImage)}
            <section className="slim">
              <div className="blog-post-body">
                {documentToReactComponents(body.json, richTextRenderOptions)}
              </div>
              <hr />
              {renderParticipants(participantsCollection.items)}
              <Link to={`${storeRoutes.writer}/${author.slug}`}>
                <div className="blog-post-author">
                  <FlexBox
                    className={css`
                      margin-bottom: 32px;
                    `}
                    alignItems="center"
                  >
                    <RoundImage
                      className="avatar"
                      src={author.avatar.url}
                      alt={author.avatar.title}
                      size={120}
                      diameter={52}
                    />
                    <Text
                      className={css`
                        text-transform: uppercase;
                      `}
                      type="label"
                      sizing="L"
                      color={colors.hurricane}
                    >
                      By {author.name}
                    </Text>
                  </FlexBox>
                  {documentToReactComponents(
                    author.bio.json,
                    descriptionStyles
                  )}
                </div>
              </Link>
            </section>
          </div>
        </section>
      </>
    )
  }

  const head = data?.blogPost
    ? {
        title: data.blogPost.title,
        description: data.blogPost.excerpt,
        image: data.blogPost.featuredImage.url,
        seoTitle: data.blogPost.seoTitle,
        seoDescription: data.blogPost.seoDescription,
        canonical: true,
      }
    : null

  return (
    <Page head={head} className="blog-detail screen-height">
      <div className="page-content fade">{renderContent()}</div>
    </Page>
  )
}

BlogDetail.propTypes = {
  location: PropTypes.object,
}

BlogDetail.defaultProps = {
  location: {},
}

export default BlogDetail

const styles = {
  bookList: css`
    @media only screen and (max-width: 548px) {
      width: calc(100% + 64px);
      margin-left: -32px;
    }
  `,
}
